import { getUser, postNpsFeedback, postNpsFeedbackComment } from '@/core/api';
import { SUBMIT_NPS_LD_EVENT_KEY } from '@/core/constants';
import {
  activeCompanySelector,
  setUser,
  userSelector,
} from '@/core/store/user/userSlice';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useNotification from '@/hooks/useNotification';
import { differenceInCalendarDays } from 'date-fns';
import * as Sentry from '@sentry/browser';
import { createContext, useState, ReactNode, useRef, useCallback } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
interface NPSModalContextType {
  isModalOpened: boolean;
  closeModal: () => void;
  openModal: (v: string) => void;
  event: string;
  isFeedbackSkipped: boolean;
  isLoading: boolean;
  score: number | undefined;
  setScore: (v: number | undefined) => void;
  isCommentInputShown: boolean;
  setIsCommentInputShown: (v: boolean) => void;
  isSkipFeedbackShown: boolean;
  setIsSkipFeedbackShown: (v: boolean) => void;
  comment: string;
  setComment: (v: string) => void;
  id: number | undefined;
  setId: (v: number | undefined) => void;
  setIsLoading: (v: boolean) => void;
  onClickNum: (v: number) => void;
  sendComment: () => void;
  onClickNotNow: () => void;
  onConfirmSkipFeedback: () => void;
}

export const NPSModalContext = createContext<NPSModalContextType>(
  {} as NPSModalContextType
);

export const NPS_MODAL_KEY = 'nps_opened';
export const NPS_TRIGGER_EVENT = 'nps_trigger_event';

export function NPSModalProvider({ children }: { children: ReactNode }) {
  const activeCompany = useAppSelector(activeCompanySelector);
  const user = useAppSelector(userSelector);
  const { showNotification } = useNotification();
  const ldClient = useLDClient();
  const dispatch = useAppDispatch();

  const eventRef = useRef(sessionStorage.getItem(NPS_TRIGGER_EVENT) || '');
  const [isFeedbackSkipped, setIsFeedbackSkipped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [score, setScore] = useState<number>();
  const [isCommentInputShown, setIsCommentInputShown] = useState(false);
  const [isSkipFeedbackShown, setIsSkipFeedbackShown] = useState(false);
  const [comment, setComment] = useState('');
  const [id, setId] = useState<number>();
  const [isModalOpened, setIsModalOpened] = useState(
    !isFeedbackSkipped &&
      (Boolean(sessionStorage.getItem(NPS_MODAL_KEY)) || false)
  );

  const closeModal = () => {
    setIsModalOpened(false);
    sessionStorage.removeItem(NPS_MODAL_KEY);
    sessionStorage.removeItem(NPS_TRIGGER_EVENT);
  };

  const lastNpsSubmission =
    user?.LastNpsSubmission && new Date(user.LastNpsSubmission);
  const createdAt = user?.CreatedAt && new Date(user.CreatedAt);
  const currentDate = new Date();

  const canSubmitNps =
    !isFeedbackSkipped &&
    Boolean(
      (!lastNpsSubmission &&
        (!createdAt ||
          differenceInCalendarDays(currentDate, createdAt) >= 90)) ||
        (lastNpsSubmission &&
          differenceInCalendarDays(currentDate, lastNpsSubmission) >= 90)
    );

  const openModal = useCallback(
    (v: string) => {
      sessionStorage.setItem(NPS_TRIGGER_EVENT, v);
      eventRef.current = v;
      if (canSubmitNps) {
        setIsModalOpened(true);
        sessionStorage.setItem(NPS_MODAL_KEY, 'true');
      }
    },
    [canSubmitNps]
  );

  const onClickNum = (v: number) => {
    if (!activeCompany) return;
    setScore(v);
    setIsLoading(true);
    postNpsFeedback(activeCompany.company_id, v)
      .then((res) => {
        setId(res.id);
        ldClient?.track(SUBMIT_NPS_LD_EVENT_KEY);
        return getUser();
      })
      .then((res) => {
        dispatch(setUser(res));
        if (v < 7) {
          setIsCommentInputShown(true);
        } else {
          closeModal();
        }
      })
      .catch((err) => {
        console.error(err);
        Sentry.captureException(err);
        showNotification({
          text: 'Something went wrong, please try again',
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const sendComment = () => {
    if (!activeCompany || !id) return;
    setIsLoading(true);
    postNpsFeedbackComment(activeCompany.company_id, id, comment)
      .then(() => {
        closeModal();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickNotNow = () => {
    setIsSkipFeedbackShown(true);
  };

  const onConfirmSkipFeedback = () => {
    setIsFeedbackSkipped(true);
    closeModal();
  };

  return (
    <NPSModalContext.Provider
      value={{
        isModalOpened,
        closeModal,
        openModal,
        event: eventRef.current,
        isFeedbackSkipped,
        isLoading,
        score,
        setScore,
        isCommentInputShown,
        setIsCommentInputShown,
        isSkipFeedbackShown,
        setIsSkipFeedbackShown,
        comment,
        setComment,
        id,
        setId,
        setIsLoading,
        onClickNum,
        sendComment,
        onClickNotNow,
        onConfirmSkipFeedback,
      }}
    >
      {children}
    </NPSModalContext.Provider>
  );
}
